import { useState, useEffect, useRef } from "react";
import { Phone, Menu, X, ChevronDown, User } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoginDropdownOpen, setIsLoginDropdownOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const loginDropdownRef = useRef<HTMLDivElement>(null);
  const servicesDropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const toggleLoginDropdown = () => setIsLoginDropdownOpen(!isLoginDropdownOpen);
  const toggleServicesDropdown = () => setIsServicesDropdownOpen(!isServicesDropdownOpen);

  // Handle click outside to close menu and dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
      if (loginDropdownRef.current && !loginDropdownRef.current.contains(event.target as Node)) {
        setIsLoginDropdownOpen(false);
      }
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target as Node)) {
        setIsServicesDropdownOpen(false);
      }
    };

    if (isMenuOpen || isLoginDropdownOpen || isServicesDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen, isLoginDropdownOpen, isServicesDropdownOpen]);

  // Handle escape key to close menu and dropdowns
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
        setIsLoginDropdownOpen(false);
        setIsServicesDropdownOpen(false);
      }
    };

    if (isMenuOpen || isLoginDropdownOpen || isServicesDropdownOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMenuOpen, isLoginDropdownOpen, isServicesDropdownOpen]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [location.pathname]);

  return (
    <nav className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50" ref={menuRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-28">
          {/* Logo */}
          <div className="flex-shrink-0">
            <a href="/">
              <img src="/whitefield-logo-1.png" alt="Whitefield Legal Services" className="h-13 w-auto" />
            </a>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-wl-gray hover:text-wl-navy transition-colors font-medium">
              Home
            </Link>
            <Link to="/about" className="text-wl-gray hover:text-wl-navy transition-colors font-medium">
              About
            </Link>
            
            {/* Services Dropdown */}
            <div 
              className="relative" 
              ref={servicesDropdownRef}
              onMouseEnter={() => setIsServicesDropdownOpen(true)}
              onMouseLeave={() => setIsServicesDropdownOpen(false)}
            >
              <button
                onClick={toggleServicesDropdown}
                className="flex items-center text-wl-gray hover:text-wl-navy transition-colors font-medium py-2"
              >
                Services
                <ChevronDown className={`w-4 h-4 ml-1 transition-transform duration-200 ${
                  isServicesDropdownOpen ? 'rotate-180' : ''
                }`} />
              </button>
              
              {/* Dropdown Menu */}
              {isServicesDropdownOpen && (
                <div className="absolute left-0 top-full w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  <Link
                    to="/services"
                    className="block px-4 py-2 text-sm text-wl-gray hover:text-wl-navy hover:bg-gray-50 transition-colors"
                    onClick={() => setIsServicesDropdownOpen(false)}
                  >
                    For Conveyancing Solicitors
                  </Link>
                  <Link
                    to="/estate-agents"
                    className="block px-4 py-2 text-sm text-wl-gray hover:text-wl-navy hover:bg-gray-50 transition-colors"
                    onClick={() => setIsServicesDropdownOpen(false)}
                  >
                    For Estate Agents
                  </Link>
                  <Link
                    to="/commercial"
                    className="block px-4 py-2 text-sm text-wl-gray hover:text-wl-navy hover:bg-gray-50 transition-colors"
                    onClick={() => setIsServicesDropdownOpen(false)}
                  >
                    For Commercial Purchases
                  </Link>
                </div>
              )}
            </div>
            
            <Link to="/newsletter" className="text-wl-gray hover:text-wl-navy transition-colors font-medium">
              Newsletter
            </Link>

            <Link to="/contact" className="text-wl-gray hover:text-wl-navy transition-colors font-medium">
              Contact
            </Link>
            
            {/* Login Dropdown */}
            <div 
              className="relative" 
              ref={loginDropdownRef}
              onMouseEnter={() => setIsLoginDropdownOpen(true)}
              onMouseLeave={() => setIsLoginDropdownOpen(false)}
            >
              <button
                onClick={toggleLoginDropdown}
                className="flex items-center text-wl-gray hover:text-wl-navy transition-colors font-medium"
              >
                <User className="w-4 h-4 mr-1" />
                Login
                <ChevronDown className={`w-4 h-4 ml-1 transition-transform duration-200 ${
                  isLoginDropdownOpen ? 'rotate-180' : ''
                }`} />
              </button>
              
              {/* Dropdown Menu */}
              {isLoginDropdownOpen && (
                <div className="absolute right-0 top-full w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  <a
                    href="https://whitefieldlegal.com/wls/client/"
                    className="block px-4 py-2 text-sm text-wl-gray hover:text-wl-navy hover:bg-gray-50 transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Admin Login
                  </a>
                  <a
                    href="https://whitefieldlegal.com/wls/solicitor/"
                    className="block px-4 py-2 text-sm text-wl-gray hover:text-wl-navy hover:bg-gray-50 transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Client Login
                  </a>
                </div>
              )}
            </div>
          </div>
          
          {/* Phone Number - Desktop */}
          <div className="hidden md:flex items-center space-x-4">
            <a 
              href="tel:01923620240" 
              className="flex items-center text-wl-navy hover:text-wl-blue transition-colors"
            >
              <Phone className="w-4 h-4 mr-2" />
              <span className="font-semibold">01923 620240/41</span>
            </a>
            <a 
              href="/contact" 
              className="bg-wl-green hover:bg-wl-green/90 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Get Quote
            </a>
          </div>
          
          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={toggleMenu}
              className="text-wl-gray hover:text-wl-navy transition-colors p-2"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Navigation Menu */}
        <div className={`md:hidden transition-all duration-300 ease-in-out overflow-hidden ${
          isMenuOpen 
            ? 'max-h-[500px] opacity-100 border-t border-gray-200' 
            : 'max-h-0 opacity-0'
        }`}>
          <div className="bg-white">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link 
                to="/" 
                className="block px-3 py-2 text-wl-gray hover:text-wl-navy hover:bg-gray-50 transition-colors font-medium rounded-md"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link 
                to="/about" 
                className="block px-3 py-2 text-wl-gray hover:text-wl-navy hover:bg-gray-50 transition-colors font-medium rounded-md"
                onClick={() => setIsMenuOpen(false)}
              >
                About
              </Link>
              {/* Services Section */}
              <div className="px-3 py-2">
                <div className="text-xs font-semibold text-wl-navy uppercase tracking-wide mb-2">
                  Services
                </div>
                <Link 
                  to="/services" 
                  className="block px-3 py-2 text-sm text-wl-gray hover:text-wl-navy hover:bg-gray-50 transition-colors rounded-md"
                  onClick={() => setIsMenuOpen(false)}
                >
                  For Conveyancing Solicitors
                </Link>
                <Link 
                  to="/estate-agents" 
                  className="block px-3 py-2 text-sm text-wl-gray hover:text-wl-navy hover:bg-gray-50 transition-colors rounded-md"
                  onClick={() => setIsMenuOpen(false)}
                >
                  For Estate Agents
                </Link>
                <Link 
                  to="/commercial" 
                  className="block px-3 py-2 text-sm text-wl-gray hover:text-wl-navy hover:bg-gray-50 transition-colors rounded-md"
                  onClick={() => setIsMenuOpen(false)}
                >
                  For Commercial Purchases
                </Link>
              </div>
              <Link
                to="/newsletter"
                className="block px-3 py-2 text-wl-gray hover:text-wl-navy hover:bg-gray-50 transition-colors font-medium rounded-md"
                onClick={() => setIsMenuOpen(false)}
              >
                Newsletter
              </Link>
              <Link 
                to="/contact" 
                className="block px-3 py-2 text-wl-gray hover:text-wl-navy hover:bg-gray-50 transition-colors font-medium rounded-md"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>
              
              {/* Login Links */}
              <div className="px-3 py-2 border-t border-gray-200">
                <div className="text-xs font-semibold text-wl-navy uppercase tracking-wide mb-2">
                  Login
                </div>
                <a 
                  href="https://whitefieldlegal.com/wls/client/"
                  className="block px-3 py-2 text-sm text-wl-gray hover:text-wl-navy hover:bg-gray-50 transition-colors rounded-md"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Admin Login
                </a>
                <a 
                  href="https://whitefieldlegal.com/wls/solicitor/"
                  className="block px-3 py-2 text-sm text-wl-gray hover:text-wl-navy hover:bg-gray-50 transition-colors rounded-md"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Client Login
                </a>
              </div>
            </div>

            {/* Mobile Contact Info */}
            <div className="px-5 py-6 mt-6 border-t border-gray-200">
              <a 
                href="/contact" 
                className="w-full flex items-center justify-center bg-wl-green hover:bg-wl-green/90 text-white px-4 py-3 rounded-lg font-medium transition-colors"
              >
                Get Quote
              </a>
              <div className="mt-6 text-center">
                <a 
                  href="tel:01923620240" 
                  className="text-wl-navy hover:text-wl-blue font-semibold"
                >
                  01923 620240/41
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}